import { FC, Fragment, useState } from "react";
import { Send } from "@mui/icons-material";
import { useAccount } from "wagmi";

import { useMetamask, useWriteContractWithSimulation } from "@dzambalaorg/react-hooks-eth";
import { ListAction, ListActionVariant } from "@framework/styled";
import { AddressType, ContractFeatures, type IToken, TokenStatus } from "@framework/types";
import ERC1155SafeTransferFromABI from "@framework/abis/json/ERC1155Simple/safeTransferFrom.json";

import { Erc1155TransferDialog, type IErc1155TransferDto } from "./dialog";

interface IErc1155TransferButtonProps {
  className?: string;
  disabled?: boolean;
  token: IToken;
  variant?: ListActionVariant;
}

export const Erc1155TransferButton: FC<IErc1155TransferButtonProps> = props => {
  const { className, disabled, token, variant = ListActionVariant.button } = props;

  const [isTransferTokenDialogOpen, setIsTransferTokenDialogOpen] = useState(false);

  const { writeContractAsync } = useWriteContractWithSimulation();
  const { address: walletAddress = "" } = useAccount();

  const { execute: metaFn } = useMetamask(async (dto: IErc1155TransferDto) => {
    return writeContractAsync({
      address: token.template!.contract!.address as AddressType,
      abi: ERC1155SafeTransferFromABI,
      functionName: "safeTransferFrom",
      args: [walletAddress, dto.account, token.tokenId, dto.amount, "0x"],
    });
  });

  const handleTransfer = (): void => {
    setIsTransferTokenDialogOpen(true);
  };

  const handleTransferConfirm = async (dto: IErc1155TransferDto) => {
    await metaFn(dto).finally(() => {
      setIsTransferTokenDialogOpen(false);
    });
  };

  const handleTransferCancel = () => {
    setIsTransferTokenDialogOpen(false);
  };

  if (token.tokenStatus === TokenStatus.BURNED) {
    return null;
  }

  return (
    <Fragment>
      <ListAction
        icon={Send}
        onClick={handleTransfer}
        message="form.buttons.transfer"
        className={className}
        dataTestId="Erc721TransferButton"
        disabled={disabled || token.template?.contract?.contractFeatures.includes(ContractFeatures.SOULBOUND)}
        variant={variant}
      />
      <Erc1155TransferDialog
        onConfirm={handleTransferConfirm}
        onCancel={handleTransferCancel}
        open={isTransferTokenDialogOpen}
        message="dialogs.transfer"
        testId="Erc721TransferDialogForm"
        initialValues={{
          account: "",
          amount: "1",
        }}
      />
    </Fragment>
  );
};
